import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import UserContext from '../context/UserContext';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../AxiosConfig';
import MainLayout from '../layouts/MainLayout';

const SubscriberRoute = ({ children }) => {
  const { user, loading } = useContext(UserContext);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (user && user.role_name === 'subscriber') {
        try {
          const token = localStorage.getItem("jwtToken");
          const response = await api.get("/get-subscription", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          setSubscription(response.data.message);
        } catch (error) {
          console.error("Error fetching subscription:", error);
          setSubscriptionError("Failed to fetch subscription data.");
        } finally {
          setSubscriptionLoading(false);
        }
      } else {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  if (loading || subscriptionLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/register" replace />;
  }

  if (user && user.role_name === 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.role_name !== 'subscriber') {
    return <Navigate to="/payment-plan" replace />;
  }

  if (subscriptionError) {
    return (
      <MainLayout>
        <div className="py-2 sm:px-4">
          <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800" style={{ fontFamily: 'Red Hat Display' }}>
                      Unable to Access Subscription
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>We couldn't retrieve your subscription information. Please try refreshing the page or contact our support team.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>

                <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Red Hat Display' }}>
                  Subscription Access Error
                </h3>

                <p className="text-gray-600 mb-6">
                  There was an issue loading your subscription details. This might be a temporary connectivity issue.
                </p>

                <div className="space-y-4">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
                    style={{ fontFamily: 'Red Hat Display' }}
                  >
                    Refresh Page
                  </button>

                  <p className="text-sm text-gray-500">
                    Still having issues? Contact us at{' '}
                    <a href="mailto:info@console-d.com" className="text-orange hover:underline">
                      info@console-d.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check subscription status and timing
  if (subscription) {
    const now = new Date();
    const startDate = new Date(subscription.start_date);
    const endDate = new Date(subscription.end_date);

    if (subscription.status !== 'active') {
      let statusMessage = '';
      let statusColor = '';
      let statusIcon = '';

      switch (subscription.status) {
        case 'expired':
          statusMessage = 'Your subscription has expired.';
          statusColor = 'text-red-600';
          statusIcon = (
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
          break;
        case 'cancelled':
          statusMessage = 'Your subscription has been cancelled.';
          statusColor = 'text-gray-600';
          statusIcon = (
            <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
          break;
        case 'suspended':
          statusMessage = 'Your subscription is currently suspended.';
          statusColor = 'text-yellow-600';
          statusIcon = (
            <svg className="mx-auto h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
          break;
        default:
          statusMessage = 'Your subscription is not currently active.';
          statusColor = 'text-gray-600';
          statusIcon = (
            <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
      }

      return (
        <MainLayout>
          <div className="py-2 sm:px-4">
            <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800" style={{ fontFamily: 'Red Hat Display' }}>
                        Access Restricted
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p className={statusColor.replace('text-', '')}>{statusMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <div className="mb-6">
                    {statusIcon}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Red Hat Display' }}>
                    Subscription Issue
                  </h3>

                  <p className={`font-semibold mb-4 ${statusColor}`}>
                    {statusMessage}
                  </p>

                  <p className="text-gray-600 mb-6">
                    You currently don't have access to CONSOLE-D. Please contact our support team for assistance or to reactivate your subscription.
                  </p>

                  <div className="space-y-4">
                    <a
                      href="/payment-plan"
                      className="block w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                      style={{ fontFamily: 'Red Hat Display' }}
                    >
                      View Subscription Plans
                    </a>

                    <p className="text-sm text-gray-500">
                      Need help? Contact us at{' '}
                      <a href="mailto:info@console-d.com" className="text-orange hover:underline">
                        info@console-d.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MainLayout>
      );
    }

    // Check if subscription hasn't started yet
    if (startDate > now) {
      const timeUntilStart = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
      return (
        <MainLayout>
          <div className="py-2 sm:px-4">
            <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800" style={{ fontFamily: 'Red Hat Display' }}>
                        Subscription Pending
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Your subscription will start in {timeUntilStart} day{timeUntilStart !== 1 ? 's' : ''}.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <div className="mb-6">
                    <svg className="mx-auto h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Red Hat Display' }}>
                    Subscription Starting Soon
                  </h3>

                  <p className="text-blue-600 font-semibold mb-4">
                    Your subscription will start in {timeUntilStart} day{timeUntilStart !== 1 ? 's' : ''}.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-gray-600 mb-2">
                      <strong>Start Date:</strong> {startDate.toLocaleDateString('en-US', { timeZone: 'Asia/Singapore', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                    </p>
                    <p className="text-gray-600">
                      <strong>Plan:</strong> {subscription.subscription_name}
                    </p>
                  </div>

                  <p className="text-gray-600 mb-6">
                    Please wait until your subscription start date to access CONSOLE-D features.
                  </p>

                  <div className="space-y-4">
                    <a
                      href="/payment-plan"
                      className="block w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                      style={{ fontFamily: 'Red Hat Display' }}
                    >
                      View My Subscription
                    </a>

                    <p className="text-sm text-gray-500">
                      Questions? Contact us at{' '}
                      <a href="mailto:info@console-d.com" className="text-orange hover:underline">
                        info@console-d.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MainLayout>
      );
    }

    // Check if subscription has expired
    if (endDate <= now) {
      return (
        <MainLayout>
          <div className="py-2 sm:px-4">
            <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800" style={{ fontFamily: 'Red Hat Display' }}>
                        Subscription Expired
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>Your subscription expired on {endDate.toLocaleDateString('en-US', { timeZone: 'Asia/Singapore', year: 'numeric', month: 'long', day: 'numeric' })}.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <div className="mb-6">
                    <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Red Hat Display' }}>
                    Subscription Expired
                  </h3>

                  <p className="text-red-600 font-semibold mb-4">
                    Your subscription expired on {endDate.toLocaleDateString('en-US', { timeZone: 'Asia/Singapore', year: 'numeric', month: 'long', day: 'numeric' })}.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-gray-600">
                      <strong>Plan:</strong> {subscription.subscription_name}
                    </p>
                  </div>

                  <p className="text-gray-600 mb-6">
                    To continue accessing CONSOLE-D, please renew your subscription or contact our support team.
                  </p>

                  <div className="space-y-4">
                    <a
                      href="/payment-plan"
                      className="block w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                      style={{ fontFamily: 'Red Hat Display' }}
                    >
                      Renew Subscription
                    </a>

                    <p className="text-sm text-gray-500">
                      Need assistance? Contact us at{' '}
                      <a href="mailto:info@console-d.com" className="text-orange hover:underline">
                        info@console-d.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MainLayout>
      );
    }
  }

  return children;
};

export default SubscriberRoute;
